import { ChevronRight } from '@material-ui/icons';
import { Stack } from '@superdispatch/ui';
import { Button } from '@superdispatch/ui-lab';
import { useFlag } from 'shared/settings/FeatureToggles';
import { trackLoadboardEvent } from '../../core/LoadboardAnalytics';
import { NearbyShipperLoadCard } from '../../core/nearby-shipper-loads/NearbyShipperLoadCard';
import {
  NEARBY_SHIPPER_LOADS_MOBILE_LIMIT,
  useNearbyShipperLoads,
  useTrackNearbyShipperLoadsViewed,
} from '../../core/nearby-shipper-loads/NearbyShipperLoadsHelpers';
import { PostingDTO } from '../../data/dto/PostingDTO';
import { useLoadboardContext } from '../../data/LoadboardContext';
import { PostingDetailsCard } from './PostingDetailsCard';

interface PostingDetailsNearbyShipperLoadsProps {
  posting: PostingDTO;
}

export function PostingDetailsNearbyShipperLoads({
  posting,
}: PostingDetailsNearbyShipperLoadsProps) {
  const isEnabled = useFlag('slb_nearby_shipper_loads');

  // The underlying search query has no `enabled` option, so the content is
  // mounted only once we know we want it.
  if (!isEnabled || !posting.load.shipper) {
    return null;
  }

  return <PostingDetailsNearbyShipperLoadsContent posting={posting} />;
}

function PostingDetailsNearbyShipperLoadsContent({
  posting,
}: PostingDetailsNearbyShipperLoadsProps) {
  const { load } = posting;
  const {
    router: { openPostingScreen, openSearchPostingsScreen },
  } = useLoadboardContext();
  const { criteria, nearbyLoads } = useNearbyShipperLoads(
    load,
    NEARBY_SHIPPER_LOADS_MOBILE_LIMIT,
  );
  const resultsCount = nearbyLoads?.length ?? 0;

  useTrackNearbyShipperLoadsViewed(posting, resultsCount);

  if (!nearbyLoads?.length) {
    return null;
  }

  function handleViewAllClick() {
    trackLoadboardEvent({
      name: 'SLB: Clicked View All Nearby Shipper Loads',
      posting,
      resultsCount,
    });

    openSearchPostingsScreen({
      tab: 'available',
      source: 'Nearby Shipper Loads',
      searchCriteria: criteria,
      strategy: 'push',
    });
  }

  return (
    <PostingDetailsCard header="Loads Nearby from the Same Shipper">
      <Stack space="small">
        {nearbyLoads.map((nearbyPosting, index) => (
          <NearbyShipperLoadCard
            key={nearbyPosting.load.guid}
            posting={nearbyPosting}
            onClick={() => {
              trackLoadboardEvent({
                name: 'SLB: Clicked Nearby Shipper Load',
                posting,
                clickedLoadGuid: nearbyPosting.load.guid,
                position: index,
              });

              openPostingScreen(nearbyPosting, 'push', {
                utm_medium: 'Nearby Shipper Loads',
                utm_content: 'Load Details',
              });
            }}
          />
        ))}

        <Button
          fullWidth={true}
          variant="neutral"
          endIcon={<ChevronRight />}
          onClick={handleViewAllClick}
        >
          View All
        </Button>
      </Stack>
    </PostingDetailsCard>
  );
}
